/**
 * ComplianceTracker — compliance document management on VendorDetail page.
 *
 * Shows a compliance status summary grid: one row per document type (8 types).
 * Each row shows: document type name, status indicator (green=valid,
 * yellow=expiring, red=expired, gray=missing), expiration date, verification
 * status, and actions. Supports add, edit, delete, and "View in Egnyte" link.
 *
 * If SOC 2 is required (vendor handles PII/Financial Data/PHI), shows a
 * prominent indicator.
 *
 * Requirements: 9.1, 9.2
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  complianceService,
  COMPLIANCE_DOCUMENT_TYPES,
  type ComplianceDocTypeStatus,
  type ComplianceStatusSummary,
} from '@/services/complianceService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ComplianceDocForm, type ComplianceDocFormData } from '@/components/ComplianceDocForm';
import type { ComplianceDocument } from '@/types';

// ─── Constants ─────────────────────────────────────────────────────────

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  valid: 'green',
  expiring: 'yellow',
  expired: 'red',
  missing: 'gray',
};

const STATUS_LABELS: Record<string, string> = {
  valid: 'Valid',
  expiring: 'Expiring',
  expired: 'Expired',
  missing: 'Missing',
};

// ─── Props ─────────────────────────────────────────────────────────────

interface ComplianceTrackerProps {
  vendorId: number;
  canWrite: boolean;
}

// ─── Component ─────────────────────────────────────────────────────────

export function ComplianceTracker({ vendorId, canWrite }: ComplianceTrackerProps) {
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState<ComplianceDocument | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ComplianceDocTypeStatus | null>(null);

  // Fetch compliance status summary
  const {
    data: statusData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['compliance-status', vendorId],
    queryFn: () => complianceService.getStatus(vendorId),
    enabled: vendorId > 0,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['compliance-status', vendorId] });
    queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: ComplianceDocFormData) =>
      complianceService.createDocument(vendorId, {
        documentType: data.documentType,
        issueDate: data.issueDate || null,
        expirationDate: data.expirationDate || null,
        verificationStatus: data.verificationStatus || null,
        egnyteFilePath: data.egnyteFilePath || null,
        egnyteFileId: data.egnyteFileId || null,
      }),
    onSuccess: () => {
      invalidate();
      closeForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ docId, data, updatedAt }: { docId: number; data: ComplianceDocFormData; updatedAt: string }) =>
      complianceService.updateDocument(docId, {
        documentType: data.documentType,
        issueDate: data.issueDate || null,
        expirationDate: data.expirationDate || null,
        verificationStatus: data.verificationStatus || null,
        egnyteFilePath: data.egnyteFilePath || null,
        egnyteFileId: data.egnyteFileId || null,
        updatedAt,
      }),
    onSuccess: () => {
      invalidate();
      closeForm();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (docId: number) => complianceService.deleteDocument(docId),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
  });

  function openCreate() {
    setEditingDoc(null);
    setShowForm(true);
  }

  async function openEdit(docStatus: ComplianceDocTypeStatus) {
    if (!docStatus.complianceDocId) return;
    try {
      const doc = await complianceService.getDocumentById(docStatus.complianceDocId);
      setEditingDoc(doc);
      setShowForm(true);
    } catch {
      // If fetch fails, open form with what we have from the status
      setEditingDoc({
        complianceDocId: docStatus.complianceDocId!,
        vendorId,
        documentType: docStatus.documentType,
        issueDate: null,
        expirationDate: docStatus.expirationDate ?? null,
        egnyteFilePath: null,
        egnyteFileId: null,
        verificationStatus: docStatus.verificationStatus ?? null,
        createdAt: '',
        updatedAt: '',
      });
      setShowForm(true);
    }
  }

  function closeForm() {
    setShowForm(false);
    setEditingDoc(null);
  }

  function handleFormSubmit(data: ComplianceDocFormData) {
    if (editingDoc) {
      updateMutation.mutate({
        docId: editingDoc.complianceDocId,
        data,
        updatedAt: editingDoc.updatedAt,
      });
    } else {
      createMutation.mutate(data);
    }
  }

  if (isLoading) {
    return (
      <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Compliance</h2>
        <LoadingSpinner message="Loading compliance status…" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Compliance</h2>
        <ErrorMessage
          message={(error as Error)?.message ?? 'Failed to load compliance status.'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const summary: ComplianceStatusSummary | undefined = statusData;
  const documents = summary?.documents ?? [];

  // Count statuses for the header summary
  const validCount = documents.filter((d) => d.status === 'valid').length;
  const expiringCount = documents.filter((d) => d.status === 'expiring').length;
  const expiredCount = documents.filter((d) => d.status === 'expired').length;
  const missingCount = documents.filter((d) => d.status === 'missing').length;

  return (
    <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
            Compliance ({COMPLIANCE_DOCUMENT_TYPES.length} document types)
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
            {validCount > 0 && <StatusBadge label={`${validCount} Valid`} variant="green" />}
            {expiringCount > 0 && <StatusBadge label={`${expiringCount} Expiring`} variant="yellow" />}
            {expiredCount > 0 && <StatusBadge label={`${expiredCount} Expired`} variant="red" />}
            {missingCount > 0 && <StatusBadge label={`${missingCount} Missing`} variant="gray" />}
          </div>
        </div>
        {canWrite && (
          <button
            onClick={openCreate}
            style={{
              padding: '0.3rem 0.75rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            + Add Document
          </button>
        )}
      </div>

      {/* SOC 2 Requirement Indicator */}
      {summary?.soc2Required && (
        <div
          style={{
            marginBottom: '0.75rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.8rem',
          }}
        >
          <span style={{ fontSize: '1rem' }} aria-hidden="true">⚠️</span>
          <span>
            <strong>SOC 2 Type II Required</strong> — This vendor handles{' '}
            <strong>{summary.dataHandlingClass}</strong> and must maintain a current SOC 2 Type II report.
          </span>
        </div>
      )}

      {/* Compliance Status Grid */}
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}
        role="table"
        aria-label="Compliance document status"
      >
        <thead>
          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>
              Document Type
            </th>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>
              Status
            </th>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>
              Expiration
            </th>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>
              Verification
            </th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#475569' }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {documents.map((docStatus) => (
            <ComplianceRow
              key={docStatus.documentType}
              docStatus={docStatus}
              canWrite={canWrite}
              onEdit={() => openEdit(docStatus)}
              onDelete={() => setDeleteTarget(docStatus)}
            />
          ))}
        </tbody>
      </table>

      {/* Compliance Document Form Modal */}
      <ComplianceDocForm
        isOpen={showForm}
        onClose={closeForm}
        onSubmit={handleFormSubmit}
        document={editingDoc}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() =>
          deleteTarget?.complianceDocId && deleteMutation.mutate(deleteTarget.complianceDocId)
        }
        title="Delete Compliance Document"
        message={`Are you sure you want to delete the "${deleteTarget?.documentType}" compliance document?`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}

// ─── Row Component ─────────────────────────────────────────────────────

interface ComplianceRowProps {
  docStatus: ComplianceDocTypeStatus;
  canWrite: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function ComplianceRow({ docStatus, canWrite, onEdit, onDelete }: ComplianceRowProps) {
  const isMissing = docStatus.status === 'missing';

  return (
    <tr
      style={{
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: isMissing ? '#fafafa' : undefined,
      }}
    >
      <td style={{ padding: '0.4rem 0.5rem', fontWeight: 500 }}>
        {docStatus.documentType}
      </td>
      <td style={{ padding: '0.4rem 0.5rem' }}>
        <StatusBadge
          label={STATUS_LABELS[docStatus.status]}
          variant={STATUS_VARIANTS[docStatus.status]}
        />
      </td>
      <td style={{ padding: '0.4rem 0.5rem' }}>
        {docStatus.expirationDate
          ? new Date(docStatus.expirationDate).toLocaleDateString()
          : '—'}
      </td>
      <td style={{ padding: '0.4rem 0.5rem' }}>
        {isMissing ? (
          <span style={{ color: '#94a3b8' }}>—</span>
        ) : (
          <StatusBadge
            label={docStatus.verificationStatus ?? 'Pending'}
            variant={
              docStatus.verificationStatus === 'Verified'
                ? 'green'
                : docStatus.verificationStatus === 'Expired'
                  ? 'red'
                  : 'yellow'
            }
          />
        )}
      </td>
      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
        <span style={{ display: 'inline-flex', gap: '0.25rem' }}>
          {!isMissing && (
            <>
              <button
                onClick={onEdit}
                style={{
                  padding: '0.2rem 0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                }}
              >
                Edit
              </button>
              {canWrite && (
                <button
                  onClick={onDelete}
                  style={{
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #fca5a5',
                    backgroundColor: '#fff',
                    color: '#dc2626',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                  }}
                >
                  Delete
                </button>
              )}
            </>
          )}
        </span>
      </td>
    </tr>
  );
}
